import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Star, Send, User, ThumbsUp, MessageSquare, 
  ChevronLeft, ChevronRight, Quote, Verified,
  Calendar, Loader2
} from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRating = ({ rating, interactive, onRate, testIdPrefix }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => interactive && onRate && onRate(star)}
        className={interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}
        disabled={!interactive}
        data-testid={`${testIdPrefix}-star-${star}-${interactive ? "button" : "display"}`}
        aria-label={`${star} star${star === 1 ? "" : "s"}`}
      >
        <Star
          className={`w-5 h-5 ${
            star <= rating
              ? "fill-yellow-400 text-yellow-400"
              : "text-gray-400"
          }`}
        />
      </button>
    ))}
  </div>
);

export default function UserReviews({ token, user, isDarkMode }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [userReview, setUserReview] = useState(null);
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: "",
    content: ""
  });
  const [stats, setStats] = useState({ average: 0, total: 0, distribution: {} });

  const reviewsPerPage = 3;

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    if (token) {
      checkUserReview();
    }
  }, [token]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data.reviews || []);
      setStats(response.data.stats || { average: 0, total: 0, distribution: {} });
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkUserReview = async () => {
    try {
      const response = await axios.get(`${API}/reviews/my-review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.review) {
        setUserReview(response.data.review);
      }
    } catch (error) {
      if (error.response?.status !== 404) console.error("Review fetch error:", error);
    }
  };

  const submitReview = async () => {
    if (!newReview.content.trim()) {
      toast.error("Please write a review");
      return;
    }
    if (newReview.content.length < 20) {
      toast.error("Review must be at least 20 characters");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/reviews`, newReview, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success("Thank you — your review is pending approval.");
        setShowForm(false);
        setNewReview({ rating: 5, title: "", content: "" });
        fetchReviews();
        checkUserReview();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const likeReview = async (reviewId) => {
    if (!token) {
      toast.error("Please log in to like reviews");
      return;
    }
    try {
      await axios.post(`${API}/reviews/${reviewId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchReviews();
    } catch (error) {
      toast.error("Failed to like review");
    }
  };

  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const displayedReviews = reviews.slice(
    currentPage * reviewsPerPage,
    (currentPage + 1) * reviewsPerPage
  );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-[#00f2ea]" />
            User Reviews
          </h2>
          <p className="text-sm opacity-60">See what our users are saying</p>
        </div>
        
        {/* Overall Rating */}
        <div className={`flex items-center gap-4 p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <div className="text-center">
            <p className="text-4xl font-black text-yellow-400" data-testid="user-reviews-average-rating">{stats.average.toFixed(1)}</p>
            <StarRating rating={Math.round(stats.average)} testIdPrefix="user-reviews-overall-rating" />
            <p className="text-xs opacity-50 mt-1" data-testid="user-reviews-total-count">{stats.total} reviews</p>
          </div>
          
          {/* Rating Distribution */}
          <div className="hidden md:block space-y-1">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = stats.distribution?.[star] || 0;
              const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-2 text-xs">
                  <span className="w-3">{star}</span>
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-6 text-right opacity-50">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Write Review Button */}
      {token && !userReview && (
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-gradient-to-r from-[#00f2ea] to-[#7000ff] hover:opacity-90"
          data-testid="user-reviews-write-review-button"
        >
          <Star className="w-4 h-4 mr-2" />
          Write a Review
        </Button>
      )}

      {/* User's Existing Review */}
      {userReview && (
        <div className={`p-4 rounded-xl border-2 border-[#00f2ea]/30 ${isDarkMode ? 'bg-[#00f2ea]/5' : 'bg-[#00f2ea]/10'}`}>
          <p className="text-xs text-[#00f2ea] font-semibold mb-2">Your Review {userReview.status === "pending" ? "• Pending approval" : ""}</p>
          <div className="flex items-start gap-3">
            <StarRating rating={userReview.rating} testIdPrefix="user-reviews-existing-review-rating" />
            <div className="flex-1">
              {userReview.title && <p className="font-semibold">{userReview.title}</p>}
              <p className="text-sm opacity-80">{userReview.content}</p>
            </div>
          </div>
        </div>
      )}

      {/* Review Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
          >
            <h3 className="font-semibold mb-4">Share Your Experience</h3>
            
            {/* Rating Selection */}
            <div className="mb-4">
              <label className="text-sm opacity-70 block mb-2">Your Rating</label>
              <StarRating 
                rating={newReview.rating} 
                interactive 
                onRate={(r) => setNewReview(prev => ({ ...prev, rating: r }))}
                testIdPrefix="user-reviews-form-rating"
              />
            </div>
            
            {/* Title */}
            <div className="mb-4">
              <label className="text-sm opacity-70 block mb-2">Title (Optional)</label>
              <input
                type="text"
                placeholder="Sum up your experience"
                value={newReview.title}
                onChange={(e) => setNewReview(prev => ({ ...prev, title: e.target.value }))}
                maxLength={100}
                className={`w-full p-3 rounded-lg border ${isDarkMode ? 'bg-black/30 border-white/10' : 'bg-white border-gray-200'}`}
                data-testid="user-reviews-title-input"
              />
            </div>
            
            {/* Review Content */}
            <div className="mb-4">
              <label className="text-sm opacity-70 block mb-2">Your Review</label>
              <Textarea
                placeholder="Tell us about your experience with TheForecastTracker..."
                value={newReview.content}
                onChange={(e) => setNewReview(prev => ({ ...prev, content: e.target.value }))}
                rows={4}
                maxLength={1000}
                className={`w-full ${isDarkMode ? 'bg-black/30 border-white/10' : 'bg-white border-gray-200'}`}
                data-testid="user-reviews-content-textarea"
              />
              <p className="text-xs opacity-50 mt-1" data-testid="user-reviews-content-character-count">{newReview.content.length}/1000 characters</p>
            </div>
            
            {/* Submit */}
            <div className="flex items-center gap-3">
              <Button
                onClick={submitReview}
                disabled={submitting}
                className="bg-[#00f2ea] hover:bg-[#00f2ea]/80 text-black"
                data-testid="user-reviews-submit-button"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Review
              </Button>
              <Button variant="ghost" onClick={() => setShowForm(false)} data-testid="user-reviews-cancel-button">
                Cancel
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reviews List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin opacity-50" />
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-4">
          {displayedReviews.map((review, idx) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
            >
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}>
                  {review.avatar ? (
                    <img src={review.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="w-6 h-6 opacity-50" />
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold">{review.display_name || "Anonymous"}</span>
                    {review.verified_purchase && (
                      <span className="flex items-center gap-1 text-xs text-green-400">
                        <Verified className="w-3 h-3" />
                        Verified
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3 mb-2">
                    <StarRating rating={review.rating} testIdPrefix={`user-review-${review.id}-rating`} />
                    <span className="text-xs opacity-50 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(review.created_at)}
                    </span>
                  </div>
                  
                  {review.title && (
                    <p className="font-medium mb-1">{review.title}</p>
                  )}
                  
                  <p className="text-sm opacity-80 mb-3">
                    <Quote className="w-4 h-4 inline mr-1 opacity-30" />
                    {review.content}
                  </p>
                  
                  {/* Like Button */}
                  <button
                    onClick={() => likeReview(review.id)}
                    className={`flex items-center gap-1 text-sm px-3 py-1 rounded-full transition-colors ${
                      review.user_liked 
                        ? 'bg-[#00f2ea]/20 text-[#00f2ea]' 
                        : 'bg-white/5 hover:bg-white/10 opacity-60 hover:opacity-100'
                    }`}
                    data-testid={`user-review-${review.id}-helpful-button`}
                  >
                    <ThumbsUp className="w-4 h-4" />
                    <span>Helpful ({review.likes || 0})</span>
                  </button>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
                data-testid="user-reviews-previous-page-button"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm opacity-60" data-testid="user-reviews-page-indicator">
                Page {currentPage + 1} of {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                disabled={currentPage === totalPages - 1}
                data-testid="user-reviews-next-page-button"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 opacity-50">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No reviews yet. Be the first to share your experience!</p>
        </div>
      )}
    </div>
  );
}
